"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Trash2, Upload } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Separator } from "@/components/ui/separator"
import { useRole } from "@/hooks/useRole"
import {
  ProjectDetail,
  KnowledgeFile,
  CreateProjectData,
  UpdateProjectData,
} from "@/services/projectService"

type ProjectSettingsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  project: ProjectDetail | null // null = create mode, non-null = edit mode
  onCreateProject: (data: CreateProjectData) => Promise<string>
  onUpdateProject: (projectId: string, data: UpdateProjectData) => Promise<void>
  onDeleteProject: (projectId: string) => Promise<void>
  onUploadKnowledgeFile: (projectId: string, file: File) => Promise<string>
  onRemoveKnowledgeFile: (projectId: string, fileKey: string) => Promise<void>
}

const ALLOWED_KNOWLEDGE_TYPES = [
  ".pdf", ".docx", ".xlsx", ".pptx", ".csv", ".txt", ".md", ".json",
].join(",")

export function ProjectSettingsDialog({
  open,
  onOpenChange,
  project,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  onUploadKnowledgeFile,
  onRemoveKnowledgeFile,
}: ProjectSettingsDialogProps) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [customInstructions, setCustomInstructions] = useState("")
  const [knowledgeFiles, setKnowledgeFiles] = useState<KnowledgeFile[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { isAdmin } = useRole()
  const isEditMode = project !== null
  const readOnly = !isAdmin

  // Populate form when dialog opens or project changes
  useEffect(() => {
    if (open) {
      if (project) {
        setName(project.name)
        setDescription(project.description || "")
        setCustomInstructions(project.customInstructions || "")
        setKnowledgeFiles(project.knowledgeFiles || [])
      } else {
        setName("")
        setDescription("")
        setCustomInstructions("")
        setKnowledgeFiles([])
      }
      setError(null)
    }
  }, [open, project])

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setError("プロジェクト名は必須です")
      return
    }
    setIsSaving(true)
    setError(null)
    try {
      if (isEditMode && project) {
        await onUpdateProject(project.projectId, {
          name: name.trim(),
          description: description.trim(),
          customInstructions: customInstructions.trim(),
        })
        onOpenChange(false)
      } else {
        await onCreateProject({
          name: name.trim(),
          description: description.trim() || undefined,
          customInstructions: customInstructions.trim() || undefined,
        })
        // Don't close — parent will switch to edit mode so user can upload knowledge files
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save project")
    } finally {
      setIsSaving(false)
    }
  }, [name, description, customInstructions, isEditMode, project, onCreateProject, onUpdateProject, onOpenChange])

  const handleDelete = useCallback(async () => {
    if (!project) return
    if (!confirm("このプロジェクトを削除しますか？セッションは「一般」に移動されます。")) return
    setIsSaving(true)
    try {
      await onDeleteProject(project.projectId)
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete project")
    } finally {
      setIsSaving(false)
    }
  }, [project, onDeleteProject, onOpenChange])

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !project) return
    setIsUploading(true)
    setError(null)
    try {
      await onUploadKnowledgeFile(project.projectId, file)
      // The parent hook refreshes currentProject which updates knowledgeFiles
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload file")
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }, [project, onUploadKnowledgeFile])

  const handleFileRemove = useCallback(async (fileKey: string) => {
    if (!project) return
    try {
      await onRemoveKnowledgeFile(project.projectId, fileKey)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove file")
    }
  }, [project, onRemoveKnowledgeFile])

  // Sync knowledgeFiles when project updates externally (after upload/remove)
  useEffect(() => {
    if (project) {
      setKnowledgeFiles(project.knowledgeFiles || [])
    }
  }, [project])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "プロジェクト設定" : "新しいプロジェクト"}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "プロジェクト設定、カスタム指示、ナレッジファイルを更新します。"
              : "エージェント用のカスタム指示を含む新しいプロジェクトを作成します。"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">名前</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="マイプロジェクト"
              maxLength={100}
              readOnly={readOnly}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">説明</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="説明（任意）"
              maxLength={500}
              readOnly={readOnly}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">カスタム指示</label>
            <Textarea
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="エージェントへのカスタム指示を追加..."
              rows={5}
              maxLength={10000}
              readOnly={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              この指示はプロジェクト内のすべての会話でエージェントのシステムプロンプトに追加されます。
            </p>
          </div>

          {isEditMode && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">ナレッジファイル</label>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <Upload className="h-3.5 w-3.5 mr-1" />
                      {isUploading ? "アップロード中..." : "アップロード"}
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={ALLOWED_KNOWLEDGE_TYPES}
                    onChange={handleFileUpload}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  エージェント用の参照ファイルをアップロードします。会話中にエージェントが参照できます。
                </p>
                {knowledgeFiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    ナレッジファイルはまだアップロードされていません。
                  </p>
                ) : (
                  <div className="space-y-1">
                    {knowledgeFiles.map((kf) => (
                      <div
                        key={kf.fileKey}
                        className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                      >
                        <span className="truncate mr-2">{kf.filename}</span>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => handleFileRemove(kf.fileKey)}
                            title="ファイルを削除"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          {isAdmin && isEditMode ? (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isSaving}
              size="sm"
            >
              プロジェクトを削除
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              {readOnly ? "閉じる" : "キャンセル"}
            </Button>
            {isAdmin && (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? "保存中..." : isEditMode ? "変更を保存" : "プロジェクトを作成"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
